import { useSelector } from 'react-redux';
import { Route, BrowserRouter as Router, Redirect, Switch } from 'react-router-dom';
import { IntlProvider } from 'react-intl';
import 'app/App.scss';
import "primereact/resources/themes/lara-light-cyan/theme.css";

import Theme from 'app/components/Theme';
import Home from 'app/screens/Home';
import Login from 'app/screens/Login';
import Auth from 'app/components/Auth';
import Jobs from 'app/components/Jobs';
import Customers from 'app/components/Customers';
import Moments from 'app/components/Moments';
import JobConfigs from 'app/components/JobConfigs';
import JobConfigTemplates from 'app/components/JobConfigTemplates';
import Members from 'app/components/Members';
import CompanyProfile from 'app/components/CompanyProfile';
import ContentProviders from 'app/components/ContentProviders';
import ConfigureApps from 'app/components/ConfigureApps';
import UserProfile from 'app/components/UserProfile';
import Kiosks from 'app/components/Kiosks';
import Marketing from 'app/components/Marketing';
import Purchases from 'app/components/Purchases';
import Uploads from 'app/components/Uploads';
import AccountBilling from 'app/components/AccountBilling';
import Billing from 'app/components/Billing';
import AccountPricing from 'app/components/AccountPricing';
import BillingReport from 'app/components/BillingReport';
import BillingExportSchedule from 'app/components/BillingExportSchedule';
import SalesReport from 'app/components/SalesReport';
import AnalyticsReport from 'app/components/AnalyticsReport';
import PhotographerHourlyReport from 'app/components/PhotographerHourlyReport';
import Accounts from 'app/components/Accounts';
import Admin from 'app/components/Admin';
import Catalogs from 'app/components/Catalogs';
import EmployeeQRGenerator from 'app/components/EmployeeQRGenerator';
import HomeRedirect from 'app/components/HomeRedirect';
import LoginLayout from 'app/screens/Login/LoginLayout';
import TwoFactorAuthentication from 'app/screens/Login/TwoFactorAuthentication';
import PasswordReset from 'app/screens/Login/PasswordReset';
import PasswordUpdate from 'app/screens/Login/PasswordUpdate';
import AcceptInvite from 'app/screens/Login/AcceptInvite';
import Guests from 'app/components/Guests';
import AccessCodePages from 'app/components/AccessCodePages';
import Gdpr from 'app/components/Gdpr';
import DailyReadReport from 'app/components/DailyReadReport';
import WorkerStatusReport from 'app/components/WorkerStatusReport';

import {
  jobs as jobsPath,
  customers as customersPath,
  moments as momentsPath,
  jobConfigs as jobConfigsPath,
  jobConfigTemplates as jobConfigTemplatesPath,
  members as membersPath,
  companyProfile as companyProfilePath,
  contentProviders as contentProvidersPath,
  configureApps as configureAppsPath,
  userProfile as userProfilePath,
  kiosks as kioskPath,
  marketing as marketingPath,
  purchases as purchasesPath,
  reports as reportsPath,
  uploads as uploadsPath,
  billing as billingPath,
  accountBillings as accountBillingPath,
  accountPricings as accountPricingPath,
  billingReport as billingReportPath,
  billingExportSchedules as billingExportSchedsPath,
  salesReport as salesReportPath,
  analyticsReport as analyticsReportPath,
  photographerHourlyReport as photographerHourlyReportPath,
  accounts as accountsPath,
  catalogs as catalogsPath,
  qrCodeGenerator as qrCodeGeneratorPath,
  login as loginPath,
  twoFactor as twoFactorPath,
  passwordReset as passwordResetPath,
  passwordUpdate as passwordUpdatePath,
  acceptInvite as acceptInvitePath,
  guests as guestsPath,
  accessCodePages as accessCodePagesPath,
  admin as adminPath,
  gdpr as gdprPath,
  dailyReadReport as dailyReadReportPath,
  adminDailyReadReport as adminDailyReadReportPath,
  workerStatusReport as workerStatusReportPath,
} from 'app/routes';

//import {
//  logPortalError
//} from 'shared/services/LogService';

import {
  darkModeBackgroundColor
} from 'app/components/Theme';

const App = () => {
  const darkMode = useSelector(state => state.portalReducer.darkMode);
  const locale = useSelector(state => state.messagesReducer.locale);
  const messages = useSelector(state => state.messagesReducer.messages);

  let appStyle = {};
  if (darkMode) {
    appStyle.background = darkModeBackgroundColor;
  }

  return (
    <div className="App" style={appStyle}>
      <IntlProvider defaultLocale="en" locale={locale} messages={messages}>
        <Router>
          <Switch>
            <Route path={loginPath}>
              <LoginLayout>
                <Route exact path={loginPath}>
                  <Login />
                </Route>
                <Route exact path={twoFactorPath}>
                  <TwoFactorAuthentication />
                </Route>
                <Route exact path={passwordResetPath}>
                  <PasswordReset />
                </Route>
                <Route exact path={passwordUpdatePath}>
                  <PasswordUpdate />
                </Route>
                <Route exact path={acceptInvitePath}>
                  <AcceptInvite />
                </Route>
              </LoginLayout>
            </Route>
            <Route path="/">
              <Auth>
                <Route exact path="/">
                  <HomeRedirect />
                </Route>
                <Theme>
                  <Home>
                    <Route exact path={jobsPath}>
                      <Jobs />
                    </Route>
                    <Route exact path={catalogsPath}>
                      <Catalogs />
                    </Route>
                    <Route exact path={customersPath}>
                      <Customers />
                    </Route>
                    <Route exact path={momentsPath}>
                      <Moments />
                    </Route>
                    <Route exact path={jobConfigsPath}>
                      <JobConfigs />
                    </Route>
                    <Route exact path={membersPath}>
                      <Members />
                    </Route>
                    <Route exact path={companyProfilePath}>
                      <CompanyProfile />
                    </Route>
                    <Route exact path={contentProvidersPath}>
                      <ContentProviders />
                    </Route>
                    <Route exact path={configureAppsPath}>
                      <ConfigureApps />
                    </Route>
                    <Route exact path={userProfilePath}>
                      <UserProfile />
                    </Route>
                    <Route exact path={marketingPath}>
                      <Marketing />
                    </Route>
                    <Route exact path={kioskPath}>
                      <Kiosks />
                    </Route>
                    <Route exact path={purchasesPath}>
                      <Purchases />
                    </Route>
                    <Route exact path={uploadsPath}>
                      <Uploads />
                    </Route>
                    <Route exact path={salesReportPath}>
                      <SalesReport />
                    </Route>
                    <Route exact path={analyticsReportPath}>
                      <AnalyticsReport />
                    </Route>
                    <Route exact path={dailyReadReportPath}>
                      <DailyReadReport />
                    </Route>
                    <Route exact path={photographerHourlyReportPath}>
                      <PhotographerHourlyReport />
                    </Route>
                    <Route exact path={qrCodeGeneratorPath}>
                      <EmployeeQRGenerator />
                    </Route>
                    <Route exact path={guestsPath}>
                      <Guests />
                    </Route>
                    <Route path={adminPath}>
                      <Admin>
                        <Route exact path={accountsPath}>
                          <Accounts />
                        </Route>
                        <Route exact path={jobConfigTemplatesPath}>
                          <JobConfigTemplates />
                        </Route>
                        <Route exact path={billingPath}>
                          <Billing />
                        </Route>
                        <Route exact path={accountBillingPath}>
                          <AccountBilling />
                        </Route>
                        <Route exact path={accountPricingPath}>
                          <AccountPricing />
                        </Route>
                        <Route exact path={billingReportPath}>
                          <BillingReport />
                        </Route>
                        <Route exact path={adminDailyReadReportPath}>
                          <DailyReadReport />
                        </Route>
                        <Route exact path={workerStatusReportPath}>
                          <WorkerStatusReport />
                        </Route>
                        <Route exact path={billingExportSchedsPath}>
                          <BillingExportSchedule />
                        </Route>
                        <Route exact path={accessCodePagesPath}>
                          <AccessCodePages />
                        </Route>
                        <Route exact path={gdprPath}>
                          <Gdpr />
                        </Route>
                      </Admin>
                    </Route>
                  </Home>
                </Theme>
              </Auth>
            </Route>
          </Switch>
        </Router>
      </IntlProvider>
    </div>
  );
}

export default App;
